import * as React from "react";
import { cn } from "../../lib/utils";

// External Portal v2 Phase 2 §4.1 — minimal, accessible Tabs primitive.
// Controlled by an active id; renders a `tablist` with `tab` buttons and
// a `tabpanel` for the active tab. Each tab exposes a stable testid
// (`tab-{id}`) and the tabpanel exposes `tabpanel-{activeId}` so tests
// can assert on the matrix without poking into roles.

export interface TabItem {
  id: string;
  label: string;
  /** When true, the tab renders with a "Coming soon" badge and is
   *  selectable (so users can read the empty state) but the underlying
   *  content is the placeholder. */
  comingSoon?: boolean;
}

interface TabsProps {
  /** Ordered tabs to render. */
  tabs: TabItem[];
  /** Currently active tab id. */
  activeId: string;
  /** Called when the user clicks a tab. */
  onChange: (id: string) => void;
  /** Optional aria-label for the tablist. */
  ariaLabel?: string;
  /** The active tab's content. The caller renders the right panel
   *  based on activeId. */
  children: React.ReactNode;
  className?: string;
}

export function Tabs({ tabs, activeId, onChange, ariaLabel, children, className }: TabsProps) {
  return (
    <div className={cn("flex flex-col", className)} data-testid="portal-tabs">
      <div
        role="tablist"
        aria-label={ariaLabel}
        className="flex flex-wrap gap-space-1 border-b border-border-light"
      >
        {tabs.map((t) => {
          const selected = t.id === activeId;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              id={`tab-${t.id}`}
              aria-selected={selected}
              aria-controls={`tabpanel-${t.id}`}
              data-testid={`tab-${t.id}`}
              data-active={selected || undefined}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(t.id)}
              className={cn(
                "inline-flex items-center gap-space-2 px-space-3 py-space-2 -mb-px border-b-2",
                "text-small font-medium transition-colors focus-visible:outline-none focus-visible:shadow-goldRing",
                selected
                  ? "border-gold text-onyx"
                  : "border-transparent text-gray-mid hover:text-onyx hover:border-border-light",
              )}
            >
              {t.label}
              {t.comingSoon ? (
                <span className="text-caption uppercase tracking-wide text-gold-shadow">
                  Coming soon
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      <div
        role="tabpanel"
        id={`tabpanel-${activeId}`}
        aria-labelledby={`tab-${activeId}`}
        data-testid={`tabpanel-${activeId}`}
        className="mt-space-4"
      >
        {children}
      </div>
    </div>
  );
}
